import React from 'react';
import { useTranslation } from 'react-i18next';
import { reportGoalIntervals, reportNumber, reportSum, reportTeamMetrics } from '../../utils/rivalReport';
import ReportComparisonRadar from './ReportComparisonRadar';

const comparisonData = (data) => {
  const statistics = data?.estadisticas;
  const metrics = reportTeamMetrics(statistics, data?.clasificacion);
  const intervals = reportGoalIntervals(statistics, data?.clasificacion);
  const games = statistics?.partidos;
  const cards = data?.tarjetas?.sourceAvailable ? data.tarjetas : null;
  return {
    wins: metrics.wins,
    draws: reportNumber(data?.clasificacion?.empatados),
    losses: reportNumber(data?.clasificacion?.perdidos),
    points: metrics.points,
    goalsFor: metrics.goalsFor,
    goalsAgainst: metrics.goalsAgainst,
    homeGoalsFor: reportSum(...intervals.map(row => row.homeFor)),
    awayGoalsFor: reportSum(...intervals.map(row => row.awayFor)),
    firstHalf: reportSum(...intervals.slice(0, 3).map(row => row.forGoals)),
    secondHalf: reportSum(...intervals.slice(3).map(row => row.forGoals)),
    cleanSheets: reportSum(games?.porteria0FinalLocal, games?.porteria0FinalVisitante),
    homeCleanSheets: reportNumber(games?.porteria0FinalLocal),
    awayCleanSheets: reportNumber(games?.porteria0FinalVisitante),
    halftimeLead: reportNumber(games?.descansoGanandoTotal),
    homeGoalsAgainst: reportSum(...intervals.map(row => row.homeAgainst)),
    yellow: reportNumber(cards?.totalAmarillas),
    red: reportNumber(cards?.totalRojas),
    homeCards: reportNumber(cards?.local),
    awayCards: reportNumber(cards?.visitante),
    leadingCards: reportNumber(cards?.ganando),
    trailingCards: reportNumber(cards?.perdiendo),
    played: metrics.played,
  };
};

export default function ReportTeamComparison({ equipoA, equipoB, datosA, datosB }) {
  const { t, i18n } = useTranslation('reports');
  const a = comparisonData(datosA);
  const b = comparisonData(datosB);
  const format = value => value === null ? '—' : value.toLocaleString(i18n.language, { maximumFractionDigits: 2 });
  const groups = [
    ['general', ['wins', 'draws', 'losses', 'points', 'goalsFor', 'goalsAgainst']],
    ['attack', ['goalsFor', 'homeGoalsFor', 'awayGoalsFor', 'firstHalf', 'secondHalf']],
    ['defense', ['cleanSheets', 'homeCleanSheets', 'awayCleanSheets', 'halftimeLead', 'goalsAgainst', 'homeGoalsAgainst']],
    ['discipline', ['yellow', 'red', 'homeCards', 'awayCards', 'leadingCards', 'trailingCards']],
  ];
  return <>
    <section className="rr-panel">
      <span className="rr-eyebrow">{t('modern.sameUnits')}</span><h2>{t('modern.comparisonTitle')}</h2>
      <div className="rr-comparison-names">
        <strong>{equipoA?.nombre}<small>{a.played === null ? t('modern.sampleUnknown') : t('modern.sample', { count: a.played })}</small></strong>
        <strong>{equipoB?.nombre}<small>{b.played === null ? t('modern.sampleUnknown') : t('modern.sample', { count: b.played })}</small></strong>
      </div>
      <p className="rr-comparison-help">{t('modern.comparisonScale')}</p>
    </section>
    <div className="rr-comparison-grid">
      {groups.map(([group, keys]) => <section className="rr-panel rr-comparison-group" key={group} aria-labelledby={`rr-compare-${group}`}>
        <header><h2 id={`rr-compare-${group}`}>{t(`modern.comparisonGroups.${group}`)}</h2></header>
        {group === 'discipline' && <p className="rr-comparison-help">{t('modern.cardSource')}</p>}
        <div className="rr-chart-legend rr-radar-legend"><span><i className="rr-swatch" />{equipoA?.nombre}</span>
          <span><i className="rr-swatch rr-swatch-against" />{equipoB?.nombre}</span></div>
        <ReportComparisonRadar keys={keys} a={a} b={b} group={group} />
        <details className="rr-comparison-bars">
          <summary>{t('modern.radarBars')}</summary>
          <div aria-hidden="true">
          {keys.map(key => {
            const max = Math.max(a[key] ?? 0, b[key] ?? 0, 1);
            return <div key={key} className="rr-compare-metric">
              <p>{t(`modern.metrics.${key}`)}</p>
              {[a[key], b[key]].map((value, index) => <div className="rr-compare-bar-row" key={index}>
                <div className="rr-compare-track"><i style={{ width: `${(value ?? 0) / max * 100}%` }} /></div><strong>{format(value)}</strong>
              </div>)}
            </div>;
          })}
          </div>
        </details>
        <div className="rr-data-table"><table aria-label={t(`modern.comparisonGroups.${group}`)}>
          <thead><tr><th>{t('modern.metric')}</th><th>{equipoA?.nombre}</th><th>{equipoB?.nombre}</th></tr></thead>
          <tbody>{keys.map(key => <tr key={key}><th scope="row">{t(`modern.metrics.${key}`)}</th>
            <td>{format(a[key])}</td><td>{format(b[key])}</td></tr>)}</tbody>
        </table></div>
      </section>)}
    </div>
  </>;
}
